import { reactive, ref } from 'vue';
import type { Reactive, Ref } from 'vue';
import mime from 'mime';
import { ENDPOINTS, skapi } from '@/main';
import { Countries } from './countries';
import { formatSpecs } from '@/code/service-spec';
import { DatabaseResponse, SkapiError } from 'skapi-js';

const regions = JSON.parse(import.meta.env.VITE_REG);
const ETC_CONFIG = JSON.parse(import.meta.env.VITE_ETC) as {
	hostDomain: string;
	target_cdn: string;
};
export const hostDomain = ETC_CONFIG?.hostDomain || 'skapi.com';
export const product_price_code = JSON.parse(import.meta.env.VITE_PRODUCT);

export type ServiceFromServer = {
	service: string;
	owner: string;

	name: string; // service name
	cors: string; // http://example.com,https://example.com
	created_locale: string; // country code ex) US. Unchangable after creation.
	description?: string; // service description
	region: string; // aws region code like "us-west-1"

	subs_id: string; // stripe subscription id
	timestamp: number; // created 13 digit timestamp

	active: 0 | 1 | -1 | -2 | -999;
	group:
		| 1 // "Trial", // currently shown as Standard
		| 2 // "Standard",
		| 3 // "Premium",
		| 50 // "Unlimited",
		| 51 // "Standard (Perpetual License)",
		| 52 // "Premium (Perpetual License)",
		| 61 // "Standard (Promo)", // reverts to 1(Trial) after one month
		| 63 // "Premium (Promo)", // reverts to 1(Trial) after three months
		| 64; // "Premium (Promo)", // reverts to 1(Trial) after six months

	email: string; // owners email

	subdomain: string; // only the subdomain part, not the full url

	users: number; // number of users in this service
	newsletter_subscribers: number; // number of public newsletter subscribers

	email_triggers: {
		template_setters: {
			welcome: string;
			confirmation: string;
			newsletter_subscription: string;
			verification: string;
			invitation: string;
		};
	};

	storage_info: {
		database: number;
		cloud: number;
		email: number;
		host: number;
		file: number;
	};

	vapid_private_key?: string;
	vapid_public_key?: string;
	api_key: string; // deprecated

	host: string;

	formattedServiceId?: string;

	email_alias?: string;
	prevent_signup?: boolean;
	prevent_inquiry?: boolean;
	freeze_database?: boolean;
	prevent_anonymous?: boolean;
	ai_agent?: string;
	suspended_at_string?: string;
	suspended_at?: number; // 13 digit timestamp

	client_secret?: { [key: string]: string }; // to be deprecated, moving to separate db
	auth_client_secret?: string[]; // to be deprecated, moving to separate db

	// -- CUSTOM ADDED FIELDS (not from server) --
	will_terminate?: string; // date string of when the service will be terminated after subscription cancellation or suspension
};

export type SubscriptionObj = {
	id: string;
	object: string;
	application: string | null;
	application_fee_percent: number | null;
	automatic_tax: {
		enabled: boolean;
		liability: string | null;
	};
	billing_cycle_anchor: number;
	billing_cycle_anchor_config: object | null;
	billing_thresholds: object | null;
	cancel_at: number | null; // actually cancelled at (10 digit timestamp)
	cancel_at_period_end: boolean;
	canceled_at: number | null; // requested cancel at (10 digit timestamp)
	cancellation_details: {
		comment: string | null;
		feedback: string | null;
		reason: string | null;
	};
	collection_method: string;
	created: number;
	currency: string;
	current_period_end: number;
	current_period_start: number;
	customer: string;
	days_until_due: number | null;
	default_payment_method: string | null;
	default_source: string | null;
	default_tax_rates: string[];
	description: string | null;
	discount: object | null;
	discounts: object[];
	ended_at: number | null;
	invoice_settings: {
		account_tax_ids: string | null;
		issuer: {
			type: string;
		};
	};
	items: object;
	latest_invoice: string;
	livemode: boolean;
	metadata: object;
	next_pending_invoice_item_invoice: string | null;
	on_behalf_of: string | null;
	pause_collection: object | null;
	payment_settings: object;
	pending_invoice_item_interval: object | null;
	pending_setup_intent: string | null;
	pending_update: object | null;
	plan: object;
	quantity: number;
	schedule: object | null;
	start_date: number;
	status: string;
	test_clock: object | null;
	transfer_data: object | null;
	trial_end: number | null;
	trial_settings: object;
	trial_start: number | null;

	// -- CUSTOM ADDED FIELDS (not from server) --
	will_cancel?: string; // date string of when the subscription will be cancelled after cancellation request
};

function randomString(length: number) {
	let result = '';
	let characters =
		'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	let charactersLength = characters.length;
	for (let i = 0; i < length; i++) {
		result += characters.charAt(
			Math.floor(Math.random() * charactersLength),
		);
	}
	return result;
}

export class Service {
	id: string;
	owner: string;
	admin_private_endpoint: string;
	record_private_endpoint: string;
	record_public_endpoint: string;
	admin_public_endpoint: string;
	service: ServiceFromServer;
	dateCreated: string;
	subscription: SubscriptionObj;

	_subsPromise;
	newsletterSender: Promise<string>[] = [];
	reserved_key: string = randomString(16);
	formattedSpecs: {
		currentPlan: {
			price: number;
			users_string: string;
			storage: {
				database: string;
				cloud: string;
				email: string;
				host: string;
				file: string;
			};
			storage_bytes: {
				database: number;
				cloud: number;
				host: number;
				email: number;
				file: number;
			};
			description: string;
			label: string;
			users: number;
			offer_list: Array<{
				class: string;
				text: string;
			}>;
			overages?: string;
		};
		serviceStatus: {
			id: string;
			original_service_id: string;
			statusCode: number;
			name: string;
			status: string;
			canChangePlan: boolean;
			users_string: string;
			users_percent: string;
			users: number;
			newsletter_subscribers_string: string;
			newsletter_subscribers: number;
			storage_bytes: {
				database?: number;
				cloud?: number;
				email?: number;
				host?: number;
				file?: number;
			};
			storage: {
				database: string;
				database_percent: string;
				cloud: string;
				cloud_percent: string;
				email: string;
				email_percent: string;
				host: string;
				host_percent: string;
				file: string;
				file_percent: string;
			};
		};
	};

	constructor(service: ServiceFromServer, endpoints: string[]) {
		this.id = service.service;
		this.admin_private_endpoint = endpoints[0];
		this.record_private_endpoint = endpoints[1];
		this.admin_public_endpoint = endpoints[2];
		this.record_public_endpoint = endpoints[3];

		this.owner = service.owner;
		this.dateCreated =
			typeof service.timestamp === 'string'
				? service.timestamp
				: new Date(service.timestamp).toDateString();

		service.formattedServiceId = skapi.util.formatServiceId(
			this.id,
			this.owner,
		);

		this.service = service;

		this.getSubdomainInfo();

		this.formattedSpecs = formatSpecs(service);
		this._subsPromise = this.getStripeSubscription();
	}

	async registerSenderEmail(params: {
		email_alias: string;
		check?: boolean;
	}): Promise<
		| 'SUCCESS: Sender e-mail has been registered.'
		| 'SUCCESS: E-mail alias is available.'
	> {
		let emailAlias: string;

		if (params && 'email_alias' in params) {
			emailAlias = params.email_alias;
		} else {
			emailAlias = '';
		}

		if (!emailAlias) {
			throw 'Email is required.';
		}

		const specialCharPattern = /[!#$%^&*(),?":{}|<>]/g;
		if (specialCharPattern.test(emailAlias)) {
			throw 'Email contains special characters.';
		}

		let res = await skapi.util.request(
			this.admin_private_endpoint + 'register-sender-email',
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ email_alias: emailAlias, check: params?.check || false },
			),
			{ auth: true },
		);

		if (!params?.check) {
			this.service.email_alias = emailAlias;
		}

		return res;
	}

	registerOpenIDLogger(params: {
		url: string; // openid token 해석 url
		mthd: string; // GET or POST
		hder: { [key: string]: string }; // header
		prms: { [key: string]: string }; // params
		data: any; // post body data (post 만 해당)
		id: string; // openid logger id
		usr: string; // user id로 사용할 openid profile attribute
		cdtn: {
			key: string; // openid profile attribute
			value: string | boolean | number; // openid profile attribute value
			condition: '=' | '>' | '<' | '>=' | '<=' | '!=' | 'ends_with';
			range: string | boolean | number; // value range
		};
		req: 'create' | 'delete' | 'update' | 'list'; // request type
	}) {
		return skapi.util.request(
			this.admin_private_endpoint + 'register-openid',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		);
	}

	async masterCreateAccount(
		form: any
	) {
		return skapi.createAccount(
			Object.assign({ service: this.id, owner: this.owner }, form)
		);
	}

	async inviteUser(
		form,
		options?: {
			confirmation_url?: string;
			email_subscription?: boolean;
		},
	): Promise<'SUCCESS: Invitation has been sent.'> {
		return skapi.inviteUser(
			Object.assign({ service: this.id, owner: this.owner }, form),
			options,
		);
	}

	async updateUserAttribute(form: { [key: string]: any }): Promise<{ [key: string]: any }> {
		let attributes = skapi.util.extractFormData(form).data;

		await skapi.util.request(
			this.admin_private_endpoint + 'admin-edit-profile',
			Object.assign({ service: this.id, owner: this.owner }, attributes),
			{ auth: true },
		);

		return attributes;
	}

	async grantAccess(params: {
		user_id: string;
		access_group: number;
	}): Promise<'SUCCESS: Access has been granted to the user.'> {
		return skapi.grantAccess(
			Object.assign({ service: this.id, owner: this.owner }, params),
		);
	}

	// get newsletter mail address
	async requestNewsletterSender(params: { group: number }): Promise<string> {
		if (this.newsletterSender[params.group]) {
			return await this.newsletterSender[params.group];
		}

		let sndr = skapi.util.request(
			this.admin_private_endpoint + 'request-newsletter-sender',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		);
		this.newsletterSender[params.group] = sndr;
		return await sndr;
	}

	async deleteNewsletter(params: { message_id: string; group: number }) {
		return skapi.util.request(
			this.admin_private_endpoint + 'delete-newsletter',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		);
	}

	async blockAccount(
		userId: string,
	): Promise<'SUCCESS: The user has been blocked.'> {
		await skapi.blockAccount(
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ user_id: userId },
			),
		);
		return 'SUCCESS: The user has been blocked.';
	}

	async unblockAccount(
		userId: string,
	): Promise<'SUCCESS: The user has been unblocked.'> {
		await skapi.unblockAccount(
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ user_id: userId },
			),
		);
		return 'SUCCESS: The user has been unblocked.';
	}

	async deleteAccount(
		userId: string,
	): Promise<'SUCCESS: Account has been deleted.'> {
		await skapi.deleteAccount(
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ user_id: userId },
			),
		);
		return 'SUCCESS: Account has been deleted.';
	}

	//send invitation email, when accepted, user will have their account created, and be redirected
	// async resendInvitation(params: { email: string; redirect: string }): Promise<'SUCCESS: Invitation E-Mail has been sent.'> {
	//   let p: any = skapi.util.extractFormData(params).data;
	//   let resend = await skapi.util.request(
	//     'confirm-signup',
	//     {
	//       service: this.id,
	//       owner: this.owner,
	//       is_invitation: p.email,
	//       redirect: p.redirect,
	//     },
	//     { auth: true }
	//   );

	//   return resend; // 'SUCCESS: Invitation E-Mail has been sent.'
	// }

	async getStripeSubscription(refresh = false): Promise<SubscriptionObj> {
		if (this.subscription && !refresh) {
			return this.subscription;
		}

		if (this.service?.subs_id) {
			let service_subs_id = this.service?.subs_id;

			// subs_id 가 배열로 올 경우에는 첫번째 값을 사용
			if (Array.isArray(service_subs_id)) {
				service_subs_id = service_subs_id[0];
			}

			let subs_id = service_subs_id.split('#');

			if (subs_id.length < 2) {
				return;
			}

			let SUBSCRIPTION_ID = subs_id[0];

			this.subscription = await skapi.clientSecretRequest({
				clientSecretName: 'stripe_test',
				url: `https://api.stripe.com/v1/subscriptions/${SUBSCRIPTION_ID}`,
				method: 'GET',
				headers: {
					Authorization: 'Bearer $CLIENT_SECRET',
					'Content-Type': 'application/x-www-form-urlencoded',
				},
			});

			let cancel_at = this.subscription?.cancel_at;
			this.service.will_terminate = (() => {
				if (
					cancel_at &&
					this.service.active < 0 &&
					this.service.active > -999
				) {
					// cancel_at is 10 digit timestamp. get the timestamp that is 3 months after cancel_at
					let cancelDate = new Date(cancel_at * 1000);
					cancelDate.setMonth(cancelDate.getMonth() + 3);
					return new Date(cancelDate.getTime()).toLocaleDateString();
				}
				return '';
			})();
			this.subscription.will_cancel =
				cancel_at && !this.service.will_terminate
					? new Date(cancel_at * 1000).toLocaleDateString()
					: '';
			if (this.service.will_terminate) {
				this.formattedSpecs.currentPlan.label = 'Canceled';
			}
		}

		if (this.service.suspended_at) {
			let suspended_at = this.service.suspended_at; // 13 digit timestamp
			let cancelDate = new Date(suspended_at);
			cancelDate.setMonth(cancelDate.getMonth() + 3);
			this.service.will_terminate = new Date(
				cancelDate.getTime(),
			).toLocaleDateString();
			this.formattedSpecs.currentPlan.label = 'Canceled';
		}

		return this.subscription;
	}

	async deleteSubscription(): Promise<SubscriptionObj> {
		if (!this.subscription) {
			return null;
		}

		if (this.service?.subs_id) {
			let service_subs_id = this.service?.subs_id;

			if (Array.isArray(service_subs_id)) {
				service_subs_id = service_subs_id[0];
			}

			let subs_id = service_subs_id.split('#');

			if (subs_id.length < 2) {
				return;
			}

			let SUBSCRIPTION_ID = subs_id[0];

			this.subscription = await skapi.clientSecretRequest({
				clientSecretName: 'stripe_test',
				url: `https://api.stripe.com/v1/subscriptions/${SUBSCRIPTION_ID}`,
				method: 'DELETE',
				headers: {
					Authorization: 'Bearer $CLIENT_SECRET',
				},
				// // data: {
				// //     cancel_at_period_end: true
				// // }
			});

			let cancel_at = this.subscription?.cancel_at;
			this.service.will_terminate = (() => {
				if (
					cancel_at &&
					this.service.active < 0 &&
					this.service.active > -999
				) {
					// cancel_at is 10 digit timestamp. get the timestamp that is 3 months after cancel_at
					let cancelDate = new Date(cancel_at * 1000);
					let now = new Date();
					cancelDate.setMonth(cancelDate.getMonth() + 3);
					let res = now >= cancelDate;

					return new Date(cancelDate.getTime()).toLocaleDateString();
				}
				return '';
			})();
			this.subscription.will_cancel =
				cancel_at && !this.service.will_terminate
					? new Date(cancel_at * 1000).toLocaleDateString()
					: '';
			if (this.service.will_terminate) {
				this.formattedSpecs.currentPlan.label = 'Canceled';
			}
		}
		return this.subscription;
	}

	async cancelSubscription(): Promise<SubscriptionObj> {
		if (!this.subscription) {
			// subscription 이 없다는 것은 subs_id 가 없다는 것, 즉 구독이 없다는 것
			throw 'no subscription';
		}

		if (this.service?.subs_id) {
			let service_subs_id = this.service?.subs_id;

			if (Array.isArray(service_subs_id)) {
				service_subs_id = service_subs_id[0];
			}

			let subs_id = service_subs_id.split('#');

			if (subs_id.length < 2) {
				return;
			}

			let SUBSCRIPTION_ID = subs_id[0];

			this.subscription = await skapi.clientSecretRequest({
				clientSecretName: 'stripe_test',
				url: `https://api.stripe.com/v1/subscriptions/${SUBSCRIPTION_ID}`,
				method: 'POST',
				headers: {
					Authorization: 'Bearer $CLIENT_SECRET',
				},
				data: {
					cancel_at_period_end: true,
				},
			});

			let cancel_at = this.subscription?.cancel_at;
			this.service.will_terminate = (() => {
				if (
					cancel_at &&
					this.service.active < 0 &&
					this.service.active > -999
				) {
					// cancel_at is 10 digit timestamp. get the timestamp that is 3 months after cancel_at
					let cancelDate = new Date(cancel_at * 1000);
					let now = new Date();
					cancelDate.setMonth(cancelDate.getMonth() + 3);
					let res = now >= cancelDate;

					return new Date(cancelDate.getTime()).toLocaleDateString();
				}
				return '';
			})();
			this.subscription.will_cancel =
				cancel_at && !this.service.will_terminate
					? new Date(cancel_at * 1000).toLocaleDateString()
					: '';
			if (this.service.will_terminate) {
				this.formattedSpecs.currentPlan.label = 'Canceled';
			}
		}

		return this.subscription;
	}

	async setServiceOption(params: {
		prevent_signup?: boolean;
		prevent_inquiry?: boolean; // true 인 경우 sendInquiry API 사용 불가능
		freeze_database?: boolean; // true 이면 데이터 베이스 구조 고정시키는 기능
		prevent_anonymous?: boolean; // true 인 경우 계정이 없는 사용자는 레코드 생성 불가능
		name?: string;
		cors?: string;
		description?: string;
		ai_agent?: string;
	}): Promise<ServiceFromServer> {
		let to_update: { [key: string]: any } = {};

		for (let key in params) {
			if (key === 'cors' && params.cors) {
				params.cors = params.cors.trim();
				// if cors have , at the end, remove it
				while (params.cors[params.cors.length - 1] === ',') {
					if (params.cors[params.cors.length - 1] === ',') {
						params.cors = params.cors.slice(0, -1);
						params.cors = params.cors.trim();
					}
				}

				let cors = params.cors.split(',').map((c) => c.trim());
				let service_cors = (this.service.cors || '')
					.split(',')
					.map((c) => c.trim());
				if (
					cors.length === service_cors.length &&
					cors.every((v, i) => v === service_cors[i])
				) {
					return this.service;
				}

				to_update.cors = cors.join(', ');
			} else if (key === 'name' && params.name !== this.service.name) {
				to_update.name = params.name;
			} else if (
				key === 'description' &&
				params.description !== this.service.description
			) {
				to_update.description = params.description;
			} else if (
				params[key as keyof typeof params] !==
				this.service[key as keyof ServiceFromServer]
			) {
				to_update[key] = params[key as keyof typeof params];
			}
		}

		if(Object.keys(to_update).length === 0) {
			return this.service;
		}

		let updated = await skapi.util.request(
			this.admin_private_endpoint + 'service-settings',
			{ service: this.id, owner: this.owner, ...to_update },
			{ auth: true },
		);
		Object.assign(this.service, to_update);
		if (typeof to_update.name === 'string') {
			this.formattedSpecs.serviceStatus.name = to_update.name;
		}
		return updated;
	}

	async registerClientSecret(params: {
		id: string | string[]; // client secret key id. should be array when req is delete
		key?: string; // client secret key value. required when req is create or update
		grp?: number; // access group number. 0 for public, 1 for locked.
		req: 'create' | 'delete' | 'update';
	}): Promise<{
		id: string | string[]; // client secret key id. should be array when req is delete
		key?: string; // client secret key value. required when req is create or update
		grp?: number; // access group number. 0 for public, 1 for locked.
		req: 'create' | 'delete' | 'update';
	}> {
		if (
			params.req === 'delete' &&
			typeof params.id === 'string' &&
			!Array.isArray(params.id)
		) {
			params.id = [params.id];
		}

		await skapi.util.request(
			this.admin_private_endpoint + 'register-client-secret',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		);

		return params;
	}

	async listClientSecrets(fetchMore: boolean = false, search?: string): Promise<
		DatabaseResponse<{
			grp: number;
			key: string;
			id: string;
		}>
	> {
		let secrets = await skapi.util.request(
			this.admin_private_endpoint + 'register-client-secret',
			{ service: this.id, owner: this.owner, req: 'list', id: search || '' },
			{
				fetchOptions: {
					limit: 30,
					fetchMore: fetchMore,
					ascending: true,
				},
				method: 'post',
				auth: true,
			},
		);
		return secrets;
	}

	async getEmailTemplate(
		params:
			| 'invitation'
			| 'newsletter_subscription'
			| 'verification'
			| 'welcome'
			| 'confirmation',
	): Promise<{
		subject: string;
		url: string;
	}> {
		let updated = await skapi.util.request(
			this.admin_private_endpoint + 'email-templates',
			{ service: this.id, owner: this.owner, template: params },
			{ auth: true },
		);
		return updated;
	}

	async enableService(): Promise<ServiceFromServer> {
		if (this.service.active === 0) {
			this.service.active = 1;
			try {
				await skapi.util.request(
					this.admin_private_endpoint + 'create-service',
					{
						service: this.id,
						owner: this.owner,
						execute: 'enable',
					},
					{ auth: true },
				);
			} catch (err: any) {
				this.service.active = 0;
				throw err;
			}
		}

		return this.service;
	}

	async disableService(): Promise<ServiceFromServer> {
		if (this.service.active > 0) {
			let orgActive = this.service.active;
			this.service.active = 0;

			try {
				await skapi.util.request(
					this.admin_private_endpoint + 'create-service',
					{
						service: this.id,
						owner: this.owner,
						execute: 'disable',
					},
					{ auth: true },
				);
			} catch (err: any) {
				this.service.active = orgActive;
				throw err;
			}
		}

		return this.service;
	}

	async deleteService(): Promise<void> {
		return skapi.util.request(
			this.admin_private_endpoint + 'create-service',
			{
				service: this.id,
				owner: this.owner,
				execute: 'delete',
			},
			{ auth: true },
		);
		// need servicelist removal from outside
	}

	async getMailTemplates(
		params?: {
			searchFor:
				| 'message_id'
				| 'timestamp'
				| 'read'
				| 'complaint'
				| 'subject';
			value: string | number;
			range?: string | number;
			condition?:
				| '>'
				| '>='
				| '='
				| '<'
				| '<='
				| 'gt'
				| 'gte'
				| 'eq'
				| 'lt'
				| 'lte';
			group:
				| 'welcome'
				| 'confirmation'
				| 'verification'
				| 'invitation'
				| 'newsletter_subscription';
		},
		fetchOptions?: { [key: string]: any },
	): Promise<any> {
		try {
			let res = await skapi.util.request(
				this.admin_private_endpoint + 'get-templates',
				Object.assign(
					{
						service: this.id,
						owner: this.owner,
					},
					params,
				),
				{ auth: true, method: 'get', fetchOptions: fetchOptions || {} },
			);

			res.list = res.list.map((value: any) => {
				return {
					message_id: value.mid,
					timestamp: value.stmp,
					subject: value.subj,
					url: value.url,
				};
			});

			return res;
		} catch (err: any) {
			throw err;
		}
	}

	async deleteTemplate(params?: {
		message_id: string;
		group:
			| 'welcome'
			| 'confirmation'
			| 'verification'
			| 'invitation'
			| 'newsletter_subscription';
	}): Promise<any> {
		try {
			return skapi.util.request(
				this.admin_private_endpoint + 'delete-template',
				Object.assign(
					{
						service: this.id,
						owner: this.owner,
					},
					params,
				),
				{ auth: true },
			);
		} catch (err: any) {
			throw err;
		}
	}

	async setTemplate(params?: {
		message_id: string;
		group:
			| 'welcome'
			| 'confirmation'
			| 'verification'
			| 'invitation'
			| 'newsletter_subscription';
	}): Promise<any> {
		try {
			return skapi.util.request(
				this.admin_private_endpoint + 'set-template',
				Object.assign(
					{
						service: this.id,
						owner: this.owner,
					},
					params,
				),
				{ auth: true },
			);
		} catch (err: any) {
			throw err;
		}
	}

	pending: {
		subdomain: false | 'removing' | 'transit' | 'fetching' | string;
		cdn: boolean; // true when cdn refresh is in process
	} = reactive({
		subdomain: false,
		cdn: false,
	});

	subdInfo = reactive({
		srvc: '',
		subd: '',
		ownr: '',
		stat: '',
		['404']: '...',
	});

	getSubdomainInfoTimer: any = null;

	dirInfo: Reactive<{
		cnt: number;
		size: number;
		upl: number;
		path: string;
	}> = reactive({
		cnt: 0,
		size: 0,
		upl: 0,
		path: '',
	});

	dbDirInfo: Reactive<{
		cnt: number;
		size: number;
		upl: number;
		path: string;
	}> = reactive({
		cnt: 0,
		size: 0,
		upl: 0,
		path: '',
	});

	async getSubdomainInfo(interval: number = 1000): Promise<{
		srvc: string;
		subd: string;
		ownr: string;
		stat:
			| 'remove'
			| 'change:(subdomain to be changed to)'
			| 'active'
			| string;
		['404']?: string;
	} | null> {
		if (this.getSubdomainInfoTimer) {
			return null;
		}

		this.pending.subdomain = 'fetching';

		let exec = async (): Promise<any> => {
			if (!this.service.service && this.service.subdomain) {
				Object.assign(this.subdInfo, {
					srvc: this.id,
					subd: '',
					ownr: this.owner,
					stat: '',
					['404']: '',
				});

				this.pending.subdomain = false;
				return this.subdInfo;
			}

			let info: any;

			try {
				info = await skapi.util.request(
					this.admin_private_endpoint + 'subdomain-info',
					{ service: this.id, owner: this.owner },
					{ auth: true },
				);
			} catch (err) {
				// retry on error
				interval *= 1.2;
				this.getSubdomainInfoTimer = setTimeout(exec, interval);
				return;
			}

			if (
				typeof info === 'string' ||
				!info ||
				(typeof info === 'object' && Object.keys(info).length === 0)
			) {
				// subdomain removed
				clearTimeout(this.getSubdomainInfoTimer);
				this.getSubdomainInfoTimer = null;
				this.pending.subdomain = false;
				this.service.subdomain = null;

				Object.assign(this.subdInfo, {
					srvc: this.id,
					subd: '',
					ownr: this.owner,
					stat: '',
					['404']: '',
				});

				return this.subdInfo;
			}

			if (info.invid) {
				this.checkCDNStatus(true);
			}

			if (info.stat) {
				// banned | remove | change:(subdomain to be changed to) | active | tracked | needs_refresh
				let pendingStat = info.stat.includes('change:')
					? 'transit'
					: info.stat.includes('remove')
						? 'removing'
						: info.stat === 'active' ||
							  info.stat === 'tracked' ||
							  info.stat === 'needs_refresh' ||
							  info.stat === 'banned'
							? false
							: info.stat;

				this.pending.subdomain = pendingStat;

				if (pendingStat) {
					// is transit or removing - keep polling
					interval *= 1.2;
					this.subdInfo.stat = info.stat;
					this.getSubdomainInfoTimer = setTimeout(exec, interval);
				} else {
					// reached final state
					clearTimeout(this.getSubdomainInfoTimer);
					this.getSubdomainInfoTimer = null;

					this.pending.subdomain = false;
					this.service.subdomain = info.subd;
					if (!info?.['404']) {
						delete this.subdInfo['404'];
					}
					Object.assign(this.subdInfo, info);
					return info;
				}
			} else {
				// no stat in response - retry
				interval *= 1.2;
				this.getSubdomainInfoTimer = setTimeout(exec, interval);
			}
		};

		this.getSubdomainInfoTimer = true; // guard before first async call
		return exec();
	}

	async registerSubdomain(params?: {
		subdomain?: string;
		check?: boolean;
	}): Promise<ServiceFromServer | { status: 'success'; resp: any }> {
		let { subdomain = '', check = false } = params || {};
		this.reserved_key = randomString(16);

		if (this.service?.subdomain === subdomain) {
			return this.service;
		}

		if (this.service?.subdomain) {
			if (subdomain && this.service?.subdomain[0] === '*') {
				throw 'Previous subdomain is in removal process.';
			} else if (subdomain && this.service?.subdomain[0] === '+') {
				throw `Previous subdomain is in transit to "${this.service.subdomain.slice(
					1,
				)}".`;
			}
		}

		// this.pending.subdomain = subdomain ? 'pending' : 'removing';
		// this.subdInfo.stat = subdomain ? 'pending' : 'removing';
		let resp = await skapi.util.request(
			this.admin_private_endpoint + 'register-subdomain',
			{ service: this.id, owner: this.owner, subdomain, check },
			{
				auth: true,
				method: 'post',
			},
		);

		// if (typeof resp !== 'string') {
		// 	this.service.subdomain = resp.subdomain;
		// 	this.subdInfo.stat = 'pending';
		// }

		if (params?.check) {
			return { status: 'success', resp }; // string, { body: string }
		}

		this.service = resp;
		await this.getSubdomainInfo();
		return resp;
	}

	async fetchCDNStatus() {
		return await skapi.util.request(
			this.admin_private_endpoint + 'refresh-cdn',
			{
				service: this.id,
				owner: this.owner,
				exec: 'status',
			},
			{
				auth: true,
				method: 'post',
			},
		);
	}

	cdnPendTimer: any = null;
	checkCDNStatus(immediate?: boolean) {
		if (this.cdnPendTimer) {
			return;
		}

		this.pending.cdn = true;
		let callbackInterval = (immediate = false) => {
			let time = immediate ? 1 : 30000;
			setTimeout(() => {
				this.fetchCDNStatus()
					.then((res) => {
						if (res === 'COMPLETE') {
							this.pending.cdn = false;
							clearTimeout(this.cdnPendTimer);
							this.cdnPendTimer = null;
							this.subdInfo.stat = 'tracked';

							return;
						}
						this.cdnPendTimer = callbackInterval();
					})
					.catch((err) => {
						this.pending.cdn = false;
						clearTimeout(this.cdnPendTimer);
						this.cdnPendTimer = null;
						return;
					});
			}, time);
		};

		this.cdnPendTimer = callbackInterval(immediate);
	}

	async refreshCDN(): Promise<
		| 'IS_QUEUED' // new cdn refresh is queued
		| 'IN_QUEUE' // the previous cdn refresh is still in queue
		| 'IN_PROCESS' // the cdn refresh is in process
	> {
		if (this.pending.cdn) {
			return 'IN_PROCESS';
		}

		if (!this.service.subdomain || this.service.subdomain[0] === '*') {
			throw 'Subdomain does not exists.';
		}

		try {
			this.pending.cdn = true;
			await skapi.util.request(
				this.admin_private_endpoint + 'refresh-cdn',
				{
					service: this.id,
					owner: this.owner,
					exec: 'refresh',
				},
				{
					auth: true,
					method: 'post',
				},
			);

			this.checkCDNStatus();
			return 'IS_QUEUED';
		} catch (err: any) {
			if (err.message === 'Previous cdn refresh is still in queue.') {
				return 'IN_QUEUE';
			}
			if (err.message === 'Previous cdn refresh is in process.') {
				return 'IN_PROCESS';
			} else {
				this.pending.cdn = false;
				if (this.cdnPendTimer) {
					clearTimeout(this.cdnPendTimer);
					this.cdnPendTimer = null;
				}
				throw err;
			}
		}
	}

	async set404(params: {
		path: string; // Set path to file of 404 page. ex) folder/file.html
	}): Promise<'SUCCESS: Removed 404 page'> {
		return await skapi.util.request(
			this.admin_private_endpoint + 'set-404',
			{ service: this.id, owner: this.owner, page404: params.path },
			{ auth: true },
		);
	}

	async uploadHostFiles(
		fileList: FormData | HTMLFormElement | SubmitEvent,
		params: {
			contentTypeMapping?: { [fileName: string]: string };
			prefix?: string;
			target?: 'db' | 'host';
			check_existence?: boolean; // check existence of file before upload, if true, it will skip the file that already exists in the target storage
			progress?: (p: {
				status: string; // 'loadstart' | 'progress' | 'abort' | 'error' | 'load' | 'timeout' | 'loadend',
				progress: number;
				currentFile: File;
				completed: File[];
				failed: File[];
				loaded: number;
				total: number;
				abort: () => void;
			}) => void;
		},
	): Promise<{ completed: File[]; failed: File[]; bin_endpoints: string[] }> {
		let progress = params?.progress || ((p: any) => p);

		if (fileList instanceof SubmitEvent) {
			fileList = fileList.target as HTMLFormElement;
		}

		if (fileList instanceof HTMLFormElement) {
			fileList = new FormData(fileList);
		}

		let reserved_key = this.reserved_key;

		let getSignedParams: Record<string, any> = {
			reserved_key,
			service: this.id,
			owner: this.owner,
			request: params?.target || 'host',
		};

		if (params?.target === 'host') {
			getSignedParams.id = this.service.subdomain;
		}

		if (params?.check_existence) {
			getSignedParams.check_existence = true;
		}

		let xhr: any;
		let fetchProgress = (
			url: string,
			body: FormData,
			progressCallback: (p: ProgressEvent) => void,
		) => {
			return new Promise((res, rej) => {
				xhr = new XMLHttpRequest();
				xhr.open('POST', url);
				xhr.onload = () => {
					let result = xhr.responseText;
					try {
						result = JSON.parse(result);
					} catch (err) {}
					if (xhr.status >= 200 && xhr.status < 300) {
						res(result);
					} else {
						rej(result);
					}
				};
				xhr.onerror = () => rej('Network error');
				xhr.onabort = () => rej('Aborted');
				xhr.ontimeout = () => rej('Timeout');

				// xhr.addEventListener('error', rej);
				if (xhr.upload && typeof progressCallback === 'function') {
					xhr.upload.onprogress = progressCallback;
				}

				xhr.onprogress = progressCallback;

				xhr.send(body);
			});
		};

		let completed = [];
		let failed = [];
		let bin_endpoints = [];
		let prefix = params.prefix ? params.prefix : '';
		prefix =
			prefix && prefix[prefix.length - 1] === '/' ? prefix : prefix + '/';

		for (let [key, f] of (fileList as any).entries()) {
			if (!(f instanceof File)) {
				continue;
			}

			let fn = (f.webkitRelativePath || f.name).split('/');
			let fns = fn[fn.length - 1];
			let ct = params?.contentTypeMapping?.[fns] || null;

			let signedParams = Object.assign(
				{
					key: prefix + (f.webkitRelativePath || f.name),
					size: f.size || 0,
					contentType: f.type || ct,
				},
				getSignedParams,
			);

			let { fields = null, url } = await skapi.util.request(
				this.record_private_endpoint + 'get-signed-url',
				signedParams,
				{ auth: true },
			);

			bin_endpoints.push(url);

			let form = new FormData();

			for (let name in fields) {
				form.append(name, fields[name]);
			}

			form.append('file', f);

			try {
				await fetchProgress(url, form, (p: ProgressEvent) =>
					progress({
						status: p.type,
						progress: (p.loaded / p.total) * 100,
						currentFile: f,
						completed,
						failed,
						loaded: p.loaded,
						total: p.total,
						abort: () => xhr.abort(),
					}),
				);
				completed.push(f);
			} catch (err) {
				failed.push(f);
			}
		}

		return { completed, failed, bin_endpoints };
	}

	async deleteHostFiles(params: {
		target?: 'db' | 'host';
		paths: string[]; // path without subdomain ex) folder/file.html
	}): Promise<string> {
		if (!params.paths || !params.paths.length) return;
		return skapi.util.request(
			this.record_private_endpoint + 'del-files',
			{
				service: this.id,
				owner: this.owner,
				endpoints: params.paths,
				reserved_key: this.reserved_key,
				storage: params.target || 'host',
			},
			{ auth: true, method: 'post' },
		);
	}

	async getDirInfo(params?: { target: 'db' | 'host' }): Promise<{
		name: string; // !
		path: string; // subdomain
		size: number;
		upl: number;
		cnt: number;
	}> {
		let dirInfo = await skapi.util.request(
			this.admin_private_endpoint + 'host-directory',
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ dir: '', info: true, target: params?.target || 'host' },
			),
			{
				fetchOptions: {
					limit: 1,
					fetchMore: false,
					ascending: true,
				},
				method: 'post',
				auth: true,
			},
		);

		if (dirInfo) {
			if (params?.target === 'db') {
				Object.assign(this.dbDirInfo, dirInfo);
			} else {
				Object.assign(this.dirInfo, dirInfo);
			}
		}
		return dirInfo;
	}

	async listHostDirectory(
		params: {
			dir: string; // unix style dir with subdomain. ex) subdomain/folder/subfolder
			target?: 'db' | 'host';
		},
		fetchMore: boolean = false,
		limit: number = 100,
	): Promise<{
		[key: string]: any;
		list: {
			name: string; // file name file.txt | #folder
			path: string; // file path ex) subdomain/folder/subfolder
			size: number;
			upl: number;
			cnt: number;
		}[];
	}> {
		let _subd = this.service?.subdomain;
		if (_subd && (_subd[0] === '*' || _subd[0] === '+')) {
			_subd = _subd.slice(1);
		}

		let dirList = await skapi.util.request(
			this.admin_private_endpoint + 'host-directory',
			Object.assign({ service: this.id, owner: this.owner }, params),
			{
				fetchOptions: {
					limit,
					fetchMore: !!fetchMore,
					ascending: true,
				},
				method: 'post',
				auth: true,
			},
		);
		return dirList;
	}

	async getTemporaryUrl(params: {
		request: 'get-host' | 'get-db' | 'get-db-zip' | 'get-host-zip';
		path: string;
		contentType?: string;
		expires: number; // seconds
		generate_redirect_url?: boolean; // generate redirect url after upload, if true, it will return the url of the uploaded file in the response
		generate_temporary_cdn_url?: boolean; // generate temporary cdn url for the uploaded file, if true, it will return the cdn url in the response
	}): Promise<{ url: string }> {
		let request = params.request || 'get-host';
		let key = params.path || '';
		let expires = params.expires || 3600;
		let contentType = params.contentType || 'application/octet-stream';
		let generate_redirect_url = params.generate_redirect_url || false;
		let generate_temporary_cdn_url = params.generate_temporary_cdn_url || false;
		// set contentType based on file extension if contentType is not provided
		if (!params.contentType) {
			contentType = mime.getType(key) || contentType;
		}

		return skapi.util
			.request(
				this.record_private_endpoint + 'get-signed-url',
				Object.assign(
					{ service: this.id, owner: this.owner },
					{
						request,
						key,
						expires,
						contentType,
						generate_redirect_url,
						generate_temporary_cdn_url,
					},
				),
				{ auth: true, method: 'post' },
			)
			.then((tempUrl: any) => {
				let filename = key.split('/').pop();
				if(filename[0] === '#') {
					filename = filename.slice(1) + '.zip';
				}
				if (request === 'get-db-zip' || request === 'get-host-zip') {
					if (tempUrl?.tmp_id) {
						let tempId = tempUrl.tmp_id;
						return this.fetchTmpZip({ key: tempId, expires, filename });
					}
				}
				if(generate_redirect_url) {
					tempUrl.url = this.record_public_endpoint + tempUrl.url;
				}
				else if(generate_temporary_cdn_url) {
					tempUrl.url = `https://db.${hostDomain}/` + tempUrl.url;
				}
				return tempUrl;
			});
	}

	async fetchTmpZip(params: {
		key: string;
		expires: number;
		filename: string;
	}): Promise<{ url: string }> {
		try {
			let resp = await skapi.util.request(
				this.record_private_endpoint + 'get-signed-url',
				Object.assign(
					{ service: this.id, owner: this.owner },
					{
						request: 'get-tmp',
						key: params.key,
						expires: params.expires,
						contentType: mime.getType(params.filename) || 'application/zip',
						filename: params.filename,
					},
				),
				{ auth: true, method: 'post' },
			);
			return resp;
		} catch (err) {
			if (err instanceof SkapiError && err.code === 'NOT_EXISTS') {
				// recurring check until the zip file is ready, with 5 seconds interval
				await new Promise((res) => setTimeout(res, 5000));
				return this.fetchTmpZip(params);
			} else {
				throw err;
			}
		}
	}
	// -------------------------------- vivians area --------------------------------

	async registerNewsletterGroup(params: {
		group: string;
		restriction: number;
	}): Promise<string> {
		Object.assign(params, { service: this.id, owner: this.owner });
		return skapi.util.request(
			this.admin_private_endpoint + 'register-newsletter-group',
			params,
			{ auth: true },
		);
	}

	async newsletterGroupEndpoint() {
		let response = await skapi.util.request(
			this.admin_private_endpoint + 'newsletter-group-endpoint',
			{ service: this.id, owner: this.owner },
			{ auth: true },
		);

		return response;
	}

	// ------------------------------- vivians area -------------------

	async refresh(onlyServiceInfo = false): Promise<ServiceFromServer> {
		let service = await skapi.util.request(
			this.admin_private_endpoint + 'pull-services',
			{
				service: skapi.service,
				owner: skapi.owner,
				service_id: this.id,
				owner_id: this.owner,
				bypass_cached_service: true,
			},
			{ auth: true },
		);
		for (let region in service) {
			if (!Array.isArray(service[region])) {
				continue;
			}

			if (service[region][0]) {
				this.service = service[region][0];
				break;
			}
		}

		if (onlyServiceInfo) {
			return this.service;
		}

		this.formattedSpecs = formatSpecs(this.service);

		let pendingStat =
			this.service.subdomain[0] === '+'
				? 'transit'
				: this.service.subdomain[0] === '*'
					? 'removing'
					: false;
		this.pending.subdomain = pendingStat;

		this.getSubdomainInfo();

		this._subsPromise = this.getStripeSubscription();

		return this.service;
	}

	static async load(srvc: ServiceFromServer): Promise<Service> {
		if (srvc && typeof srvc === 'object') {
			let {
				admin_private_endpoint,
				admin_public_endpoint,
				record_private_endpoint,
				record_public_endpoint,
			} = await ENDPOINTS;

			let inst = new Service(srvc, [
				admin_private_endpoint,
				record_private_endpoint,
				admin_public_endpoint,
				record_public_endpoint,
			]);

			await inst._subsPromise;

			return inst;
		} else {
			throw new Error('Invalid service object.');
		}
	}
}

export async function createService(params: {
	name: string;
	description?: string;
}): Promise<ServiceFromServer> {
	if (!params?.name) throw new Error('Invalid service name.');

	let { admin_private_endpoint } = await ENDPOINTS;

	let currentLocale = skapi.connection.locale;
	let serviceRegion = '';

	if (regions?.[currentLocale]) {
		serviceRegion = regions[currentLocale];
	} else {
		const calculateDistance = (locale: any, region: any) => {
			const R = 6371e3; // metres
			const φ1 = (locale.latitude * Math.PI) / 180; // φ, λ in radians
			const φ2 = (region.latitude * Math.PI) / 180;
			const Δφ = ((region.latitude - locale.latitude) * Math.PI) / 180;
			const Δλ = ((region.longitude - locale.longitude) * Math.PI) / 180;

			const a =
				Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
				Math.cos(φ1) *
					Math.cos(φ2) *
					Math.sin(Δλ / 2) *
					Math.sin(Δλ / 2);
			const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

			const d = R * c; // in metres

			return d;
		};

		let difference = null;
		for (let region in regions) {
			let distance = calculateDistance(
				Countries[currentLocale],
				Countries[region],
			);
			if (difference == null || distance < difference) {
				difference = distance;
				serviceRegion = regions[region];
			}
		}
	}

	let service = await skapi.util.request(
		admin_private_endpoint + 'create-service',
		Object.assign(params, {
			service: skapi.service,
			owner: skapi.owner,
			execute: 'create',
			region: serviceRegion,
			spec: 'BunnyQuery'
		}),
		{ auth: true },
	);

	return service as ServiceFromServer;
}
